import {assert} from 'Asserts';
import {distribution} from 'metrics/Metrics';
import {
  clearProfilerTimingEvent,
  getProfilerEventValue,
  measureTimingBetweenEvents,
  processTimingEvent,
} from 'performance/BenchlingProfiler';

import {ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

const DOMAIN = ProfilerEventDomain.ANALYSES;
/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_ANALYSES_EVENTS_VERSION = 1;

enum ProfilerAnalysesFeatures {
  APPLY_ANALYSIS_TEMPLATE = 'applyAnalysisTemplate',
  COMBINE_TABLES = 'combineTables',
  FILTER_TABLE = 'filterTable',
  MODAL_SUPPORTED_STEP = 'modalSupportedStep',
  REGRESSION = 'regression',
  RUN_ANALYSIS_TEMPLATE = 'runAnalysisTemplateRun',
  SAVE_TO_RESULTS = 'saveToResults',
  SEND_TO_DOCUMENT = 'sendToDocument',
}

enum ProfilerAnalysesActions {
  APPLY_ANALYSIS_TEMPLATE_STARTED = 'applyAnalysisTemplateStarted',
  APPLY_ANALYSIS_TEMPLATE_FINISHED = 'applyAnalysisTemplateFinished',
  COMBINE_TABLES_STARTED = 'combineTablesStarted',
  COMBINE_TABLES_FINISHED = 'combineTablesFinished',
  FILTER_TABLE_STARTED = 'filterTableStarted',
  FILTER_TABLE_FINISHED = 'filterTableFinished',
  MODAL_SUPPORTED_STEP_STARTED = 'modalSupportedStepStarted',
  MODAL_SUPPORTED_STEP_CREATED = 'modalSupportedStepCreated',
  MODAL_SUPPORTED_STEP_FINISHED = 'modalSupportedStepFinished',
  REGRESSION_STARTED = 'regressionStarted',
  REGRESSION_FINISHED = 'regressionFinished',
  RUN_ANALYSIS_TEMPLATE_STARTED = 'runAnalysisTemplateRunStarted',
  RUN_ANALYSIS_TEMPLATE_FINISHED = 'runAnalysisTemplateRunFinished',
  SAVE_TO_RESULTS_STARTED = 'saveToResultsStarted',
  SAVE_TO_RESULTS_FINISHED = 'saveToResultsFinished',
  SEND_TO_DOCUMENT_STARTED = 'sendToDocumentStarted',
  SEND_TO_DOCUMENT_FINISHED = 'sendToDocumentFinished',
}

const FEATURE_TO_PROCESSING_FUNCTION: {[key in ProfilerAnalysesFeatures]: Function} = {
  [ProfilerAnalysesFeatures.APPLY_ANALYSIS_TEMPLATE]: processApplyAnalysisTemplate,
  [ProfilerAnalysesFeatures.COMBINE_TABLES]: processCombineTables,
  [ProfilerAnalysesFeatures.FILTER_TABLE]: processFilterTable,
  [ProfilerAnalysesFeatures.MODAL_SUPPORTED_STEP]: processModalSupportedStep,
  [ProfilerAnalysesFeatures.REGRESSION]: processRegression,
  [ProfilerAnalysesFeatures.RUN_ANALYSIS_TEMPLATE]: processRunAnalysisTemplate,
  [ProfilerAnalysesFeatures.SAVE_TO_RESULTS]: processSaveToResults,
  [ProfilerAnalysesFeatures.SEND_TO_DOCUMENT]: processSendToDocument,
};

export const APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.APPLY_ANALYSIS_TEMPLATE,
  action: ProfilerAnalysesActions.APPLY_ANALYSIS_TEMPLATE_STARTED,
};

export const APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.APPLY_ANALYSIS_TEMPLATE,
  action: ProfilerAnalysesActions.APPLY_ANALYSIS_TEMPLATE_FINISHED,
};

export const COMBINE_TABLES_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.COMBINE_TABLES,
  action: ProfilerAnalysesActions.COMBINE_TABLES_STARTED,
};

export const COMBINE_TABLES_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.COMBINE_TABLES,
  action: ProfilerAnalysesActions.COMBINE_TABLES_FINISHED,
};

export const FILTER_TABLE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.FILTER_TABLE,
  action: ProfilerAnalysesActions.FILTER_TABLE_STARTED,
};

export const FILTER_TABLE_FINISHED_EVENT = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.FILTER_TABLE,
  action: ProfilerAnalysesActions.FILTER_TABLE_FINISHED,
};

export const MODAL_SUPPORTED_STEP_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.MODAL_SUPPORTED_STEP,
  action: ProfilerAnalysesActions.MODAL_SUPPORTED_STEP_STARTED,
};

export const MODAL_SUPPORTED_STEP_CREATED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.MODAL_SUPPORTED_STEP,
  action: ProfilerAnalysesActions.MODAL_SUPPORTED_STEP_CREATED,
};

export const MODAL_SUPPORTED_STEP_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.MODAL_SUPPORTED_STEP,
  action: ProfilerAnalysesActions.MODAL_SUPPORTED_STEP_FINISHED,
};

export const REGRESSION_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.REGRESSION,
  action: ProfilerAnalysesActions.REGRESSION_STARTED,
};

export const REGRESSION_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.REGRESSION,
  action: ProfilerAnalysesActions.REGRESSION_FINISHED,
};

export const RUN_ANALYSIS_TEMPLATE_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.RUN_ANALYSIS_TEMPLATE,
  action: ProfilerAnalysesActions.RUN_ANALYSIS_TEMPLATE_STARTED,
};

export const RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.RUN_ANALYSIS_TEMPLATE,
  action: ProfilerAnalysesActions.RUN_ANALYSIS_TEMPLATE_FINISHED,
};

export const SAVE_TO_RESULTS_STARTED_EVENT = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.SAVE_TO_RESULTS as ProfilerAnalysesFeatures.SAVE_TO_RESULTS,
  action: ProfilerAnalysesActions.SAVE_TO_RESULTS_STARTED,
} as const satisfies ProfilerEvent;

export const SAVE_TO_RESULTS_FINISHED_EVENT = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.SAVE_TO_RESULTS as ProfilerAnalysesFeatures.SAVE_TO_RESULTS,
  action: ProfilerAnalysesActions.SAVE_TO_RESULTS_FINISHED,
} as const;

export const SEND_TO_DOCUMENT_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.SEND_TO_DOCUMENT as ProfilerAnalysesFeatures.SEND_TO_DOCUMENT, // enum of length of 1 requires this hack
  action: ProfilerAnalysesActions.SEND_TO_DOCUMENT_STARTED,
};

export const SEND_TO_DOCUMENT_FINISHED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerAnalysesFeatures.SEND_TO_DOCUMENT as ProfilerAnalysesFeatures.SEND_TO_DOCUMENT,
  action: ProfilerAnalysesActions.SEND_TO_DOCUMENT_FINISHED,
};

export function processAnalysesEvents(profilerEvent: Pick<ProfilerEvent, 'feature' | 'action'>): void {
  const feature = profilerEvent.feature as ProfilerAnalysesFeatures;
  assert(
    FEATURE_TO_PROCESSING_FUNCTION.hasOwnProperty(feature),
    `Unexpected profiler feature: ${profilerEvent.feature}`
  );
  FEATURE_TO_PROCESSING_FUNCTION[feature](profilerEvent);
}

function processApplyAnalysisTemplate(): void {
  processTimingEvent(
    APPLY_ANALYSIS_TEMPLATE_STARTED_EVENT,
    APPLY_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    'Analysis Template Applied',
    'perf.apply_analysis_template',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}

function processCombineTables(
  profilerEvent: typeof COMBINE_TABLES_STARTED_EVENT | typeof COMBINE_TABLES_FINISHED_EVENT
): void {
  if (profilerEvent.action === ProfilerAnalysesActions.COMBINE_TABLES_STARTED) {
    return;
  }

  const measurement = measureTimingBetweenEvents(COMBINE_TABLES_STARTED_EVENT, profilerEvent);
  if (measurement == null) {
    return;
  }
  const {time, endEvent: endEventValue} = measurement;

  const {combinationType, joinType} = (endEventValue.extra ?? {
    combineType: null,
    joinType: null,
  }) as {
    combinationType: string | null;
    joinType: string | null;
  };

  distribution('perf.combine_tables', time, {combinationType, joinType});
}

function processFilterTable(): void {
  processTimingEvent(
    FILTER_TABLE_STARTED_EVENT,
    FILTER_TABLE_FINISHED_EVENT,
    'Analysis Filter Table',
    'perf.filter_table',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}

function processModalSupportedStep(): void {
  const stepCreatedEventValue = getProfilerEventValue(MODAL_SUPPORTED_STEP_CREATED_EVENT);
  const stepFinishedEventValue = getProfilerEventValue(MODAL_SUPPORTED_STEP_FINISHED_EVENT);

  if (
    stepFinishedEventValue?.extra?.stepId != null &&
    stepFinishedEventValue.extra.stepId === stepCreatedEventValue?.extra?.stepId
  ) {
    clearProfilerTimingEvent(MODAL_SUPPORTED_STEP_CREATED_EVENT);
    processTimingEvent(
      MODAL_SUPPORTED_STEP_STARTED_EVENT,
      MODAL_SUPPORTED_STEP_FINISHED_EVENT,
      'Analyses Modal-Supported Step',
      'perf.analyses_modal_supported_step',
      PROFILER_ANALYSES_EVENTS_VERSION
    );
  }
}

function processRegression(): void {
  processTimingEvent(
    REGRESSION_STARTED_EVENT,
    REGRESSION_FINISHED_EVENT,
    'Analyses Regression',
    'perf.analyses_regression',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}

function processRunAnalysisTemplate(): void {
  processTimingEvent(
    RUN_ANALYSIS_TEMPLATE_STARTED_EVENT,
    RUN_ANALYSIS_TEMPLATE_FINISHED_EVENT,
    'Analysis Template Run',
    'perf.run_analysis_template',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}

function processSaveToResults(): void {
  processTimingEvent(
    SAVE_TO_RESULTS_STARTED_EVENT,
    SAVE_TO_RESULTS_FINISHED_EVENT,
    'Analyses Save to Results',
    'perf.analyses_save_to_results',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}

function processSendToDocument(): void {
  processTimingEvent(
    SEND_TO_DOCUMENT_STARTED_EVENT,
    SEND_TO_DOCUMENT_FINISHED_EVENT,
    'Analyses Send to Document',
    'perf.analyses_send_to_document',
    PROFILER_ANALYSES_EVENTS_VERSION
  );
}
